import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { UserRole } from '@gencay-akademi/shared-types'
import { useAuthStore } from '@/stores/auth'

/**
 * Per-route role allow-list.
 *
 * - `meta.roles` undefined  → any authenticated user may enter
 * - `meta.roles: []`        → same (treated as "no restriction")
 * - `meta.roles: [...]`     → only those roles may enter; others get /403
 *
 * `meta.guest: true` is for login-only routes that authenticated users skip.
 */
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    guest?: boolean
    roles?: readonly UserRole[]
  }
}

const STAFF: readonly UserRole[] = ['admin', 'teacher']
const ADMIN: readonly UserRole[] = ['admin']
const TEACHER_ONLY: readonly UserRole[] = ['admin', 'teacher']
const STUDENT_ONLY: readonly UserRole[] = ['admin', 'student']
const PARENT_ONLY: readonly UserRole[] = ['admin', 'parent']
const TEACHER_OR_STUDENT: readonly UserRole[] = ['admin', 'teacher', 'student']
const STUDENT_OR_PARENT: readonly UserRole[] = ['admin', 'student', 'parent']

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/features/auth/LoginPage.vue'),
    meta: { guest: true },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/features/auth/ForgotPasswordPage.vue'),
    meta: { guest: true },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/features/auth/ResetPasswordPage.vue'),
    meta: { guest: true },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/features/auth/RegisterPage.vue'),
    meta: { guest: true },
  },
  {
    path: '/403',
    name: 'forbidden',
    component: () => import('@/features/auth/ForbiddenPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/features/dashboard/DashboardPage.vue'),
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/features/profile/ProfilePage.vue'),
      },
      {
        path: 'users',
        name: 'users',
        component: () => import('@/features/users/UsersPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'users/:id',
        name: 'user-detail',
        component: () => import('@/features/users/UserDetailPage.vue'),
        meta: { roles: ADMIN },
      },

      // ============================================
      // Gencay Akademi — academy domain pages (STAFF)
      // ============================================
      {
        path: 'students',
        name: 'students',
        component: () => import('@/features/students/StudentsPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'students/new',
        name: 'student-new',
        component: () => import('@/features/students/StudentFormPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'students/:id',
        name: 'student-detail',
        component: () => import('@/features/students/StudentDetailPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'students/:id/edit',
        name: 'student-edit',
        component: () => import('@/features/students/StudentFormPage.vue'),
        meta: { roles: ADMIN },
      },

      {
        path: 'teachers',
        name: 'teachers',
        component: () => import('@/features/teachers/TeachersPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'teachers/new',
        name: 'teacher-new',
        component: () => import('@/features/teachers/TeacherFormPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'teachers/:id',
        name: 'teacher-detail',
        component: () => import('@/features/teachers/TeacherDetailPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'teachers/:id/edit',
        name: 'teacher-edit',
        component: () => import('@/features/teachers/TeacherFormPage.vue'),
        meta: { roles: ADMIN },
      },

      {
        path: 'parents',
        name: 'parents',
        component: () => import('@/features/parents/ParentsPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'parents/new',
        name: 'parent-new',
        component: () => import('@/features/parents/ParentFormPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'parents/:id',
        name: 'parent-detail',
        component: () => import('@/features/parents/ParentDetailPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'parents/:id/edit',
        name: 'parent-edit',
        component: () => import('@/features/parents/ParentFormPage.vue'),
        meta: { roles: ADMIN },
      },

      {
        path: 'curriculum',
        name: 'curriculum',
        component: () => import('@/features/curriculum/CurriculumPage.vue'),
        meta: { roles: STAFF },
      },

      {
        path: 'courses',
        name: 'courses',
        component: () => import('@/features/courses/CoursesPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'private-courses',
        name: 'private-courses',
        component: () => import('@/features/courses/PrivateCoursesPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'courses/new',
        name: 'course-new',
        component: () => import('@/features/courses/CourseFormPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        // Öğrenci de aldığı eğitimi açıp içeriği (özellikle görüntülü/canlı ders
        // ve zoom linkini) görebilmeli (müşteri rev.). Sayfa yönetim kontrollerini
        // staff-dışına gizler; backend GET zaten auth-only. Veli hariç.
        path: 'courses/:id',
        name: 'course-detail',
        component: () => import('@/features/courses/CourseDetailPage.vue'),
        meta: { roles: TEACHER_OR_STUDENT },
      },
      {
        path: 'courses/:id/edit',
        name: 'course-edit',
        component: () => import('@/features/courses/CourseFormPage.vue'),
        meta: { roles: STAFF },
      },

      {
        path: 'camps',
        name: 'camps',
        component: () => import('@/features/camps/CampsListPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'camps/new',
        name: 'camp-new',
        component: () => import('@/features/camps/CampFormPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'camps/applications',
        name: 'camp-applications',
        component: () => import('@/features/camps/CampApplicationsPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'camps/:id',
        name: 'camp-detail',
        component: () => import('@/features/courses/CourseDetailPage.vue'),
        meta: { roles: STAFF },
      },
      {
        path: 'camps/:id/edit',
        name: 'camp-edit',
        component: () => import('@/features/camps/CampFormPage.vue'),
        meta: { roles: STAFF },
      },

      // Assessment (Faz 3)
      {
        path: 'question-bank',
        name: 'question-bank',
        component: () => import('@/features/question-bank/QuestionBankPage.vue'),
        meta: { roles: STAFF },
      },
      {
        path: 'paper-maker',
        name: 'paper-maker',
        component: () => import('@/features/paper-maker/PaperMakerPage.vue'),
        meta: { roles: STAFF },
      },
      {
        path: 'exams',
        name: 'exams',
        component: () => import('@/features/exams/ExamsPage.vue'),
        meta: { roles: STAFF },
      },
      {
        path: 'exams/new',
        name: 'exam-new',
        component: () => import('@/features/exams/ExamFormPage.vue'),
        meta: { roles: STAFF },
      },
      {
        path: 'exams/:id',
        name: 'exam-detail',
        component: () => import('@/features/exams/ExamDetailPage.vue'),
        meta: { roles: STAFF },
      },
      {
        path: 'exams/:id/take',
        name: 'exam-take',
        component: () => import('@/features/exams/TakeExamPage.vue'),
        // Any authenticated user can take an exam (admin preview, student attempt)
      },
      {
        // Düzeltme 9 · F3 — öğrenci "Sınavlarım" listesinden kendi cevap
        // raporuna ulaşır. Sahiplik kontrolü API tarafında (attempt.studentUserId).
        path: 'exam-attempts/:attemptId/report',
        name: 'exam-attempt-report',
        component: () => import('@/features/exams/ExamAttemptReportPage.vue'),
      },
      {
        path: 'exams/:id/edit',
        name: 'exam-edit',
        component: () => import('@/features/exams/ExamFormPage.vue'),
        meta: { roles: STAFF },
      },
      {
        path: 'exams/:id/report',
        name: 'exam-report',
        component: () => import('@/features/exams/ExamReportPage.vue'),
        meta: { roles: STAFF },
      },
      {
        path: 'homeworks',
        name: 'homeworks',
        component: () => import('@/features/homeworks/HomeworksPage.vue'),
        meta: { roles: STAFF },
      },
      {
        path: 'homeworks/:id/submissions',
        name: 'homework-submissions',
        component: () => import('@/features/homeworks/HomeworkSubmissionsPage.vue'),
        meta: { roles: STAFF },
      },
      {
        path: 'homeworks/:id/solve',
        name: 'homework-solve',
        component: () => import('@/features/homeworks/SolveHomeworkPage.vue'),
      },
      {
        path: 'worksheets',
        name: 'worksheets',
        component: () => import('@/features/worksheets/WorksheetsPage.vue'),
        meta: { roles: STAFF },
      },
      {
        path: 'worksheets/:id/report',
        name: 'worksheet-report',
        component: () => import('@/features/worksheets/WorksheetReportPage.vue'),
        meta: { roles: STAFF },
      },
      {
        path: 'worksheets/:id',
        name: 'worksheet-detail',
        component: () => import('@/features/worksheets/SolveWorksheetPage.vue'),
        // Any authenticated user can open (staff preview, student solve)
      },
      {
        path: 'worksheets/:id/solve',
        name: 'worksheet-solve',
        component: () => import('@/features/worksheets/SolveWorksheetPage.vue'),
      },
      {
        // Global cross-student attendance management — depends on the admin
        // student list (/students, adminOnly). Teachers use /my-attendance.
        // (Was STAFF; teachers got 403 on the student fetch → admin-only.)
        path: 'attendance',
        name: 'attendance',
        component: () => import('@/features/attendance/AttendancePage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'reports',
        name: 'reports',
        component: () => import('@/features/reports/ReportsPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'reports/students/:id',
        name: 'report-student-detail',
        component: () => import('@/features/reports/StudentReportDetailPage.vue'),
        meta: { roles: STAFF },
      },
      {
        path: 'guidance',
        name: 'guidance',
        component: () => import('@/features/guidance/GuidancePage.vue'),
        meta: { roles: STAFF },
      },
      {
        path: 'guidance/:id',
        name: 'guidance-detail',
        component: () => import('@/features/guidance/GuidanceGroupDetailPage.vue'),
        meta: { roles: STAFF },
      },
      {
        path: 'installments',
        name: 'installments',
        component: () => import('@/features/installments/InstallmentsPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'payments',
        name: 'payments',
        component: () => import('@/features/payments/PaymentsPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'payments-by-course',
        name: 'payments-by-course',
        component: () => import('@/features/payments/PaymentsByCoursePage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'announcements',
        name: 'announcements',
        component: () => import('@/features/announcements/AnnouncementsPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'web/settings',
        name: 'web-settings',
        component: () => import('@/features/web-content/SiteSettingsPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'web/home',
        name: 'web-home',
        component: () => import('@/features/web-content/HomeContentPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'web/courses',
        name: 'web-courses',
        component: () => import('@/features/web-content/WebCoursesPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'web/teachers',
        name: 'web-teachers',
        component: () => import('@/features/web-content/WebTeachersPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'web/about',
        name: 'web-about',
        component: () => import('@/features/web-content/AboutPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'web/pages',
        name: 'web-pages',
        component: () => import('@/features/web-content/PagesContentPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'web/messages',
        name: 'web-messages',
        component: () => import('@/features/web-content/MessagesPage.vue'),
        meta: { roles: ADMIN },
      },
      {
        path: 'payments/:studentId',
        name: 'payment-detail',
        component: () => import('@/features/payments/StudentPaymentDetailPage.vue'),
        meta: { roles: ADMIN },
      },

      // ============================================
      // Role-scoped /me/* views (D4 teacher + shared)
      // ============================================
      {
        path: 'my-courses',
        name: 'my-courses',
        component: () => import('@/features/my/MyCoursesPage.vue'),
        meta: { roles: TEACHER_OR_STUDENT },
      },
      {
        path: 'my-camps',
        name: 'my-camps',
        component: () => import('@/features/my/MyCampsPage.vue'),
        meta: { roles: STUDENT_OR_PARENT },
      },
      {
        path: 'my-students',
        name: 'my-students',
        component: () => import('@/features/my/MyStudentsPage.vue'),
        meta: { roles: TEACHER_ONLY },
      },
      {
        path: 'my-exams',
        name: 'my-exams',
        component: () => import('@/features/my/MyExamsPage.vue'),
        meta: { roles: TEACHER_OR_STUDENT },
      },
      {
        path: 'my-homeworks',
        name: 'my-homeworks',
        component: () => import('@/features/my/MyHomeworksPage.vue'),
        meta: { roles: TEACHER_OR_STUDENT },
      },
      {
        path: 'my-attendance',
        name: 'my-attendance',
        component: () => import('@/features/my/MyAttendancePage.vue'),
        meta: { roles: TEACHER_OR_STUDENT },
      },
      {
        path: 'my-worksheets',
        name: 'my-worksheets',
        component: () => import('@/features/my/MyWorksheetsPage.vue'),
        meta: { roles: STUDENT_ONLY },
      },
      {
        path: 'my-profile',
        name: 'my-profile',
        component: () => import('@/features/my/MyProfilePage.vue'),
        meta: { roles: STUDENT_ONLY },
      },
      {
        path: 'my-guidance',
        name: 'my-guidance',
        component: () => import('@/features/my/MyGuidancePage.vue'),
        meta: { roles: STUDENT_ONLY },
      },
      {
        path: 'my-self-tests',
        name: 'my-self-tests',
        component: () => import('@/features/my/MySelfTestsPage.vue'),
        meta: { roles: STUDENT_ONLY },
      },
      {
        path: 'my-error-notebook',
        name: 'my-error-notebook',
        component: () => import('@/features/my/MyErrorNotebookPage.vue'),
        meta: { roles: STUDENT_ONLY },
      },
      {
        path: 'my-program',
        name: 'my-program',
        component: () => import('@/features/my/MyProgramPage.vue'),
        meta: { roles: STUDENT_ONLY },
      },
      {
        path: 'my-payments',
        name: 'my-payments',
        component: () => import('@/features/my/MyPaymentsPage.vue'),
        meta: { roles: STUDENT_OR_PARENT },
      },
      {
        path: 'my-invoices',
        name: 'my-invoices',
        component: () => import('@/features/my/MyInvoicesPage.vue'),
        meta: { roles: STUDENT_OR_PARENT },
      },
      {
        path: 'my-announcements',
        name: 'my-announcements',
        component: () => import('@/features/my/MyAnnouncementsPage.vue'),
        meta: { roles: STUDENT_OR_PARENT },
      },
      {
        path: 'my-children',
        name: 'my-children',
        component: () => import('@/features/my/MyChildrenPage.vue'),
        meta: { roles: PARENT_ONLY },
      },
      {
        path: 'my-children/:id',
        name: 'my-child-detail',
        component: () => import('@/features/my/MyChildDetailPage.vue'),
        meta: { roles: PARENT_ONLY },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/features/auth/NotFoundPage.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Navigation guards
router.beforeEach(async (to) => {
  const authStore = useAuthStore()

  // Initialize auth state from storage. Wrap in try/catch so corrupted tokens
  // or transient API failures don't freeze navigation (audit-2026-05-09 admin/H6).
  if (!authStore.initialized) {
    try {
      await authStore.initialize()
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn('[router] auth initialize failed; clearing auth state', err)
      }
      authStore.clearAuth?.()
    }
  }

  // Login page when not authenticated
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // Authenticated user trying to hit /login → bounce to dashboard
  if (to.meta.guest && authStore.isAuthenticated) {
    return { name: 'dashboard' }
  }

  // Role-based access. /403 itself must be reachable to display the error,
  // otherwise we'd ping-pong indefinitely.
  if (
    authStore.isAuthenticated &&
    to.name !== 'forbidden' &&
    !authStore.hasAnyRole(to.meta.roles)
  ) {
    return { name: 'forbidden' }
  }

  return true
})

export default router
